import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What are your hours?",
  "Where are you located?",
  "How much is the oxtail dinner?",
  "Do you make custom cakes?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "Welcome to Uncle Willie's! I'm Uncle Willie. Ask me about our menu, hours, where to find us, or custom cakes.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        message: msg,
        session_id: sessionId,
      });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "Sorry about that, we're having trouble right now. Please call us at (904) 677-9924!",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[90] flex items-center gap-2 btn-primary px-5 py-3.5 rounded-full shadow-[0_16px_36px_-12px_rgba(130,44,21,0.7)] font-semibold"
        aria-label="Chat with Uncle Willie"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
        <span className="hidden sm:inline">
          {open ? "Close" : "Ask Uncle Willie"}
        </span>
      </button>

      {/* Panel */}
      <div
        className={`fixed bottom-24 right-4 sm:right-6 z-[90] w-[calc(100vw-2rem)] sm:w-[380px] transition-all duration-300 origin-bottom-right ${
          open
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="bg-[#FBF6EA] rounded-3xl overflow-hidden shadow-2xl border border-[#DCCFB4] flex flex-col h-[70vh] max-h-[560px]">
          {/* Header */}
          <div className="bg-[#16110E] text-[#F3EAD7] px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D5521B] flex items-center justify-center shrink-0">
              <Sparkles size={18} className="text-[#F0C766]" />
            </div>
            <div>
              <p className="font-display font-bold text-lg leading-none">
                Uncle Willie
              </p>
              <p className="label-mono text-[#7FB4C2] mt-1">
                Ask me anything · Online
              </p>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 grain-bg"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#D5521B] text-[#FBF6EA] rounded-br-sm"
                      : "bg-white text-[#16110E] border border-[#DCCFB4] rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#DCCFB4] px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="w-2 h-2 rounded-full bg-[#D5521B] animate-bounce"
                      style={{ animationDelay: `${d * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="pt-2 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#CDBE9E] text-[#5E5548] hover:bg-[#16110E] hover:text-[#F3EAD7] hover:border-[#16110E] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#DCCFB4] bg-[#FBF6EA]">
            <div className="flex items-center gap-2 bg-white border border-[#DCCFB4] rounded-full pl-4 pr-1.5 py-1.5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Type your question…"
                className="flex-1 bg-transparent outline-none text-sm text-[#16110E] placeholder:text-[#A99C86]"
              />
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                className="btn-primary w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-40 shrink-0"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
